/**
 * Filtre d'entree pour tout lot d'enregistrements chiffres venant de HORS de
 * l'appareil (serveur de synchro, fichier d'export) : seuls ceux qui
 * s'authentifient sous la `vaultKey` en format v2 (contenu ET metadonnees,
 * `aad.ts`) sont retenus. A appliquer AVANT `mergeVaultRecords`, qui tranche
 * sur des metadonnees claires : un enregistrement forge (revision gonflee,
 * faux tombstone) ne doit jamais pouvoir gagner une fusion.
 *
 * Le rejeu d'une ANCIENNE version authentique (revision inferieure) est, lui,
 * refuse par la fusion avec l'etat local (`resolveConflict` garde la revision
 * la plus haute) : l'appelant doit donc fusionner avec son etat local, jamais
 * remplacer celui-ci par la reponse du serveur.
 */
import type { SecretKey } from '@klavox/crypto-core';
import { decryptVaultAttachment, type EncryptedVaultAttachment } from './attachment.js';
import { decryptVaultFolder, type EncryptedVaultFolder } from './folder.js';
import { decryptVaultItem, type EncryptedVaultItem } from './item.js';

export interface AuthenticatedVaultRecords {
  readonly items: EncryptedVaultItem[];
  readonly folders: EncryptedVaultFolder[];
  readonly attachments: EncryptedVaultAttachment[];
  /** Nombre d'enregistrements refuses (alteres, mal formes, autre coffre ou format v1). */
  readonly rejected: number;
}

export function authenticateVaultRecords(input: {
  readonly items?: readonly EncryptedVaultItem[];
  readonly folders?: readonly EncryptedVaultFolder[];
  readonly attachments?: readonly EncryptedVaultAttachment[];
  readonly vaultKey: SecretKey;
}): AuthenticatedVaultRecords {
  const authentic = (open: () => unknown): boolean => {
    try {
      open();
      return true;
    } catch {
      // Aucun detail propage : pas d'oracle sur la cause du refus.
      return false;
    }
  };
  const inputItems = input.items ?? [];
  const inputFolders = input.folders ?? [];
  const inputAttachments = input.attachments ?? [];
  const items = inputItems.filter((item) => authentic(() => decryptVaultItem({ item, vaultKey: input.vaultKey })));
  const folders = inputFolders.filter((folder) =>
    authentic(() => decryptVaultFolder({ folder, vaultKey: input.vaultKey })),
  );
  const attachments = inputAttachments.filter((attachment) =>
    authentic(() => decryptVaultAttachment({ attachment, vaultKey: input.vaultKey })),
  );
  const rejected =
    inputItems.length - items.length + inputFolders.length - folders.length + inputAttachments.length - attachments.length;
  return { items, folders, attachments, rejected };
}
